import React, { useEffect, useState } from "react";
import { api, getErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Gear, MagnifyingGlass, Kanban as KanbanIcon, ListBullets, Tray, ChartBar } from "@phosphor-icons/react";
import CrmLeadDrawer from "@/components/crm/CrmLeadDrawer";
import NewLeadDialog from "@/components/crm/NewLeadDialog";
import CrmDashboard from "@/components/crm/CrmDashboard";
import { CRM_STAGES, STAGE_MAP, CRM_SOURCES, fmtMoney, serviceLabel, sourceLabel } from "@/lib/crm";

function LeadCard({ lead, onOpen, draggable }) {
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => e.dataTransfer.setData("lead_id", lead.lead_id)}
      onClick={() => onOpen(lead.lead_id)}
      data-testid={`crm-card-${lead.lead_id}`}
      className="cursor-pointer border border-[#E5E7EB] bg-white p-3 text-xs shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-[#030712]">{lead.prospect_name}</div>
        {lead.value > 0 && <div className="font-mono font-bold text-emerald-700">{fmtMoney(lead.value)}</div>}
      </div>
      <div className="mt-1 text-[#6B7280]">{serviceLabel(lead.service_type)}</div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {lead.overdue && (
          <span className="bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white" data-testid={`crm-overdue-${lead.lead_id}`}>
            Overdue
          </span>
        )}
        {lead.sla_state === "breached" && (
          <span className="bg-red-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">SLA!</span>
        )}
        {lead.sla_state === "warning" && (
          <span className="bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">SLA ⚠</span>
        )}
        <span className="bg-[#F3F4F6] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#4B5563]">
          {sourceLabel(lead.source)}
        </span>
        <span className="text-[10px] text-[#9CA3AF]">
          {lead.assigned_va_name || "Unassigned"} · {(lead.created_at || "").slice(5, 10)}
        </span>
      </div>
    </div>
  );
}

export default function AdminCRM() {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState("kanban");
  const [filterVa, setFilterVa] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [q, setQ] = useState("");
  const [openLead, setOpenLead] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filterVa) params.set("va", filterVa);
      if (filterSource) params.set("source", filterSource);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/crm/board?${params.toString()}`);
      setBoard(data);
    } catch (e) { setErr(getErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterVa, filterSource]);

  const loadSettings = async () => {
    try { const { data } = await api.get("/crm/settings"); setSettings(data); } catch (e) { toast.error(getErr(e)); }
  };
  useEffect(() => { if (showSettings && !settings) loadSettings(); /* eslint-disable-next-line */ }, [showSettings]);

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/crm/settings", {
        dormant_enabled: !!settings.dormant_enabled,
        dormant_flag_days: Number(settings.dormant_flag_days),
        dormant_recycle_days: Number(settings.dormant_recycle_days),
        sla_hours: Number(settings.sla_hours),
      });
      setSettings(data);
      toast.success("CRM settings saved");
      setShowSettings(false);
    } catch (e) { toast.error(getErr(e)); }
  };

  const onDrop = async (e, stage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("lead_id");
    if (!leadId) return;
    if (stage === "contacted" || stage === "lost") {
      setOpenLead(leadId);
      toast.info(stage === "lost" ? "Pick a loss reason in the drawer" : "Log the first outreach note in the drawer");
      return;
    }
    try {
      await api.post(`/crm/leads/${leadId}/stage`, { stage });
      toast.success(`Moved to ${STAGE_MAP[stage].label}`);
      load();
    } catch (er) { toast.error(getErr(er)); }
  };

  const allLeads = board ? board.stages.flatMap((s) => s.leads) : [];

  return (
    <div className="p-4 md:p-8" data-testid="admin-crm">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono-label">Work Pipeline</div>
          <h1 className="font-display text-3xl font-black tracking-tight md:text-4xl">CRM — one pipeline of record</h1>
          <p className="mt-1 text-sm text-[#4B5563]">
            Open pipeline value: <strong className="text-emerald-700">{board ? fmtMoney(board.total_open_value) : "…"}</strong>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowNew(true)} data-testid="crm-new-lead-btn"
            className="inline-flex h-9 items-center gap-1.5 bg-[#0044FF] px-3 text-xs font-semibold text-white hover:bg-[#0037CC]">
            <Plus size={14} weight="bold" /> New lead
          </button>
          <button onClick={() => setShowSettings(true)} data-testid="crm-settings-btn"
            className="inline-flex h-9 items-center gap-1.5 border border-[#030712] px-3 text-xs font-semibold hover:bg-[#F9FAFB]">
            <Gear size={14} /> Settings
          </button>
          <div className="flex border border-[#030712]">
            <button onClick={() => setView("kanban")} data-testid="crm-view-kanban"
              className={`inline-flex h-9 items-center gap-1 px-3 text-xs font-semibold ${view === "kanban" ? "bg-[#030712] text-white" : ""}`}>
              <KanbanIcon size={14} /> Board
            </button>
            <button onClick={() => setView("list")} data-testid="crm-view-list"
              className={`inline-flex h-9 items-center gap-1 px-3 text-xs font-semibold ${view === "list" ? "bg-[#030712] text-white" : ""}`}>
              <ListBullets size={14} /> List
            </button>
            <button onClick={() => setView("dashboard")} data-testid="crm-view-dashboard"
              className={`inline-flex h-9 items-center gap-1 px-3 text-xs font-semibold ${view === "dashboard" ? "bg-[#030712] text-white" : ""}`}>
              <ChartBar size={14} /> Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex h-9 items-center gap-1 border border-[#030712] bg-white px-2">
          <MagnifyingGlass size={14} className="text-[#6B7280]" />
          <input data-testid="crm-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Name / phone / email…"
            className="w-44 text-xs outline-none" />
        </div>
        <select data-testid="crm-filter-va" value={filterVa} onChange={(e) => setFilterVa(e.target.value)}
          className="h-9 border border-[#030712] bg-white px-2 text-xs">
          <option value="">All VPs</option>
          {(board?.vas || []).map((v) => <option key={v.user_id} value={v.user_id}>{v.name}</option>)}
        </select>
        <select data-testid="crm-filter-source" value={filterSource} onChange={(e) => setFilterSource(e.target.value)}
          className="h-9 border border-[#030712] bg-white px-2 text-xs">
          <option value="">All sources</option>
          {CRM_SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {err && <div className="mb-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="crm-error">{err}</div>}

      {/* Unassigned queue */}
      {board && (board.unassigned || []).length > 0 && (
        <div className="mb-4 border border-amber-400 bg-amber-50 p-3" data-testid="crm-unassigned-queue">
          <div className="mb-2 flex items-center gap-2 font-mono-label text-amber-800">
            <Tray size={14} /> Unassigned queue — {board.unassigned.length} lead{board.unassigned.length === 1 ? "" : "s"} waiting
          </div>
          <div className="flex flex-wrap gap-2">
            {board.unassigned.slice(0, 15).map((l) => (
              <div key={l.lead_id} className="flex items-center gap-2 border border-amber-300 bg-white p-2 text-xs" data-testid={`crm-unassigned-${l.lead_id}`}>
                <button onClick={() => setOpenLead(l.lead_id)} className="font-semibold underline">{l.prospect_name}</button>
                <span className="text-[#6B7280]">{sourceLabel(l.source)}</span>
                <select defaultValue="" data-testid={`crm-quick-assign-${l.lead_id}`}
                  onChange={async (e) => {
                    try {
                      await api.post(`/crm/leads/${l.lead_id}/assign`, { va_user_id: e.target.value });
                      toast.success("Assigned — attribution locked");
                      load();
                    } catch (er) { toast.error(getErr(er)); }
                  }}
                  className="h-7 border border-[#030712] bg-white px-1 text-[11px]">
                  <option value="" disabled>Assign…</option>
                  {(board.vas || []).map((v) => <option key={v.user_id} value={v.user_id}>{v.name}</option>)}
                </select>
              </div>
            ))}
            {board.unassigned.length > 15 && (
              <div className="flex items-center px-2 text-xs text-amber-800" data-testid="crm-unassigned-more">
                +{board.unassigned.length - 15} more — use List view to see all
              </div>
            )}
          </div>
        </div>
      )}

      {!board ? (
        <div className="font-mono-label">Loading…</div>
      ) : view === "dashboard" ? (
        <CrmDashboard isOwner={!!user?.is_owner} />
      ) : view === "kanban" ? (
        <div className="flex gap-3 overflow-x-auto pb-3" data-testid="crm-kanban">
          {board.stages.map((col) => (
            <div key={col.stage} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, col.stage)}
              className="w-64 shrink-0" data-testid={`crm-col-${col.stage}`}>
              <div className="mb-2 flex items-center justify-between border-b-4 pb-1" style={{ borderColor: STAGE_MAP[col.stage].color }}>
                <div className="text-xs font-black uppercase tracking-widest">{col.label}</div>
                <div className="font-mono text-[10px] text-[#4B5563]">{col.count} · {fmtMoney(col.value_total)}</div>
              </div>
              <div className="space-y-2">
                {col.leads.map((l) => <LeadCard key={l.lead_id} lead={l} onOpen={setOpenLead} draggable />)}
                {col.leads.length === 0 && <div className="border border-dashed border-[#E5E7EB] p-3 text-center text-[10px] text-[#9CA3AF]">Empty</div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white" data-testid="crm-list">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-[#4B5563]">
                <th className="px-3 py-2">Lead</th><th className="px-3 py-2">Stage</th>
                <th className="px-3 py-2">Service</th><th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Owner</th><th className="px-3 py-2 text-right">Value</th>
                <th className="px-3 py-2">Next action</th><th className="px-3 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {allLeads.map((l) => (
                <tr key={l.lead_id} onClick={() => setOpenLead(l.lead_id)}
                  className="cursor-pointer border-t border-[#E5E7EB] hover:bg-[#F9FAFB]" data-testid={`crm-list-row-${l.lead_id}`}>
                  <td className="px-3 py-2 font-semibold">{l.prospect_name}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white" style={{ backgroundColor: STAGE_MAP[l.crm_stage].color }}>
                      {STAGE_MAP[l.crm_stage].label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">{serviceLabel(l.service_type)}</td>
                  <td className="px-3 py-2 text-xs">{sourceLabel(l.source)}</td>
                  <td className="px-3 py-2 text-xs">{l.assigned_va_name || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{l.value > 0 ? fmtMoney(l.value) : "—"}</td>
                  <td className="px-3 py-2 text-xs">{l.next_action_date || "—"}</td>
                  <td className="px-3 py-2 text-xs">{(l.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openLead && (
        <CrmLeadDrawer leadId={openLead} role="admin" isOwner={!!user?.is_owner}
          vas={board?.vas || []} onClose={() => setOpenLead(null)} onChanged={load} />
      )}
      {showNew && (
        <NewLeadDialog role="admin" vas={board?.vas || []}
          onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      )}

      {/* Settings dialog */}
      {showSettings && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowSettings(false)}>
          <div className="w-full max-w-sm bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="crm-settings-dialog">
            <div className="font-display text-lg font-black">CRM automation settings</div>
            {!settings ? <div className="mt-4 font-mono-label">Loading…</div> : (
              <div className="mt-4 space-y-3 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" data-testid="crm-dormant-toggle" checked={!!settings.dormant_enabled}
                    onChange={(e) => setSettings({ ...settings, dormant_enabled: e.target.checked })}
                    className="h-4 w-4 accent-[#0044FF]" />
                  <span>Enable dormant automation</span>
                </label>
                <label className="block text-xs">
                  <span className="text-[#6B7280]">Flag Dormant after (days of no activity)</span>
                  <input type="number" min="1" data-testid="crm-dormant-flag-days" value={settings.dormant_flag_days}
                    onChange={(e) => setSettings({ ...settings, dormant_flag_days: e.target.value })}
                    className="mt-1 h-9 w-full border border-[#030712] px-2" />
                </label>
                <label className="block text-xs">
                  <span className="text-[#6B7280]">Recycle to unassigned pool after (days)</span>
                  <input type="number" min="1" data-testid="crm-dormant-recycle-days" value={settings.dormant_recycle_days}
                    onChange={(e) => setSettings({ ...settings, dormant_recycle_days: e.target.value })}
                    className="mt-1 h-9 w-full border border-[#030712] px-2" />
                </label>
                <label className="block text-xs">
                  <span className="text-[#6B7280]">First-contact SLA (business hours)</span>
                  <input type="number" min="0.5" step="0.5" data-testid="crm-sla-hours" value={settings.sla_hours}
                    onChange={(e) => setSettings({ ...settings, sla_hours: e.target.value })}
                    className="mt-1 h-9 w-full border border-[#030712] px-2" />
                </label>
                <div className="text-[10px] text-[#6B7280]">Dormant + SLA automation activates in Phase 2 — thresholds configured here.</div>
                <button onClick={saveSettings} data-testid="crm-settings-save-btn"
                  className="h-9 w-full bg-[#030712] text-xs font-bold uppercase tracking-widest text-white">Save settings</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
